import { createFileRoute } from '@tanstack/react-router';
import { Grid, Stack, Text } from '@mantine/core';
import { useState } from 'react';
import { modals } from '@mantine/modals';
import { useDeployPackageToAllSystems, useDeployPackageToSystems } from '@/features/deploymentManagement/api/packageQueries';
import { DeploymentLayout } from '@/features/deploymentManagement/components/DeploymentLayout';
import { SystemSelectorDropdown } from '@/features/deploymentManagement/components/Deploy/SystemSelectorDropdown';
import { SelectedSystemsTable } from '@/features/deploymentManagement/components/Deploy/SelectedSystemsTable';
import { DeploymentActionCard } from '@/features/deploymentManagement/components/Deploy/DeploymentActionCard';
import { ListSystemDto } from '@/features/systems/domain/systems.types';
import { IconRocket } from '@tabler/icons-react';

export const Route = createFileRoute('/_authed/admin/deployment-center/deploy/')({
  component: DeployTestPage
});

function DeployTestPage() {
  const [selectedPackageId, setSelectedPackageId] = useState<string>('');
  const [selectedSystems, setSelectedSystems] = useState<ListSystemDto[]>([]);

  const deployMutation = useDeployPackageToSystems();
  const deployToAllMutation = useDeployPackageToAllSystems();

  const handleSystemRemove = (systemId: string) => {
    setSelectedSystems(selectedSystems.filter(s => s.id !== systemId));
  };

  const handleDeployClick = () => {
    if (!selectedPackageId || selectedSystems.length === 0) return;

    modals.openConfirmModal({
      title: 'Confirm Deployment',
      children: (
        <Text size="sm">
          Deploy this package to{' '}
          <Text span fw={500}>{selectedSystems.length}</Text>{' '}
          {selectedSystems.length === 1 ? 'system' : 'systems'}?
        </Text>
      ),
      labels: {confirm: 'Deploy', cancel: 'Cancel'},
      confirmProps: {color: 'blue', leftSection: <IconRocket size={16}/>},
      onConfirm: () => {
        deployMutation.mutate({
          packageId: selectedPackageId,
          systemIds: selectedSystems.map(s => s.id)
        });
      },
    });
  };

  const handleDeployToAllClick = (packageId: string) => {
    if (!packageId) return;

    modals.openConfirmModal({
      title: 'Confirm Mass Deployment',
      children: (
        <Stack gap="sm">
          <Text size="sm" c="orange">
            ⚠️ This will deploy the selected package to{' '}
            <Text span fw={700}>ALL systems</Text> in the entire fleet.
          </Text>
          <Text size="sm">
            This action cannot be undone. Are you sure you want to proceed?
          </Text>
        </Stack>
      ),
      labels: {confirm: 'Deploy to ALL', cancel: 'Cancel'},
      confirmProps: {color: 'orange', leftSection: <IconRocket size={16}/>},
      onConfirm: () => {
        deployToAllMutation.mutate({
          packageId: packageId
        });
      },
    });
  };

  return (
    <DeploymentLayout>
      <Grid>
        <Grid.Col span={8}>
          <Stack gap="md">
            <SystemSelectorDropdown
              selectedSystems={selectedSystems}
              onSelectionChange={setSelectedSystems}
            />
            <SelectedSystemsTable
              selectedSystems={selectedSystems}
              onSystemRemove={handleSystemRemove}
              newTargetVersion={selectedPackageId ? 'Selected Package' : undefined}
            />
          </Stack>
        </Grid.Col>
        <Grid.Col span={4}>
          <DeploymentActionCard
            selectedSystemCount={selectedSystems.length}
            selectedPackageId={selectedPackageId}
            onPackageChange={setSelectedPackageId}
            onDeploy={handleDeployClick}
            onDeployToAll={handleDeployToAllClick}
            deployLoading={deployMutation.isPending}
          />
        </Grid.Col>
      </Grid>
    </DeploymentLayout>
  );
}